import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { agentKeys } from "../queryKeys";

interface DangerZonePageProps {
  agentId: string;
}

export function DangerZonePage({ agentId }: DangerZonePageProps) {
  const queryClient = useQueryClient();
  const { data: agent } = useQuery({
    queryKey: agentKeys.detail(agentId),
    queryFn: () =>
      apiFetch<{ agent: { display_name?: string; name: string } }>(`/api/agents/${agentId}`).then(
        (r) => r.agent
      ),
  });
  const agentName = agent?.display_name || agent?.name || "this agent";

  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [confirmationText, setConfirmationText] = useState("");

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch(`/api/agents/${agentId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.list() });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
      window.location.href = "/dashboard";
    },
    onError: (err: Error) => setError(err.message || "Failed to delete agent"),
  });

  const deleting = deleteMutation.isPending;

  function handleDelete() {
    setError("");
    deleteMutation.mutate();
  }

  function openConfirm() {
    setError("");
    setConfirmationText("");
    setShowConfirm(true);
  }

  function closeConfirm() {
    if (!deleting) {
      setShowConfirm(false);
      setConfirmationText("");
    }
  }

  const isConfirmationValid = confirmationText.trim().toLowerCase() === "i understand";

  return (
    <div className="general-settings">
      <div className="general-settings-form-panel">
        <div className="general-settings-form-inner">
          <div className="general-settings-page-header">
            <h1>Danger zone</h1>
            <p className="page-subtitle">
              Irreversible actions for this agent. Proceed with caution.
            </p>
          </div>

          <div className="general-settings-form">
            <section className="settings-section">
              <h4 className="section-header">
                <span className="material-symbols-outlined">delete_forever</span>
                Delete agent
              </h4>
              <p className="form-tip" style={{ marginBottom: "1rem" }}>
                Permanently delete this agent and all its data (conversations, messages, knowledge
                sources). The embed code on your website will stop working. This cannot be undone.
              </p>
              <button
                type="button"
                className="btn btn-danger"
                onClick={openConfirm}
                disabled={deleting}
              >
                <span className="material-symbols-outlined">delete_forever</span>
                Delete agent
              </button>
            </section>
          </div>

          {error && <div className="auth-error">{error}</div>}
        </div>
      </div>

      {showConfirm && (
        <div className="modal-overlay" onClick={closeConfirm}>
          <div className="modal-content danger-zone-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete this agent?</h3>
            <p>
              You are about to permanently delete{" "}
              <strong>{agentName || "this agent"}</strong>. All conversations, messages, and
              knowledge sources will be removed. The widget will no longer work on your website.
            </p>
            <p className="modal-warning">This action cannot be undone.</p>
            <div className="danger-zone-confirmation-input">
              <label htmlFor="confirmation-input">
                Type <strong>"I understand"</strong> to confirm:
              </label>
              <input
                id="confirmation-input"
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="I understand"
                disabled={deleting}
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeConfirm}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting || !isConfirmationValid}
              >
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
